/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @tanstack/query/exhaustive-deps --
   Keep the data API out of query keys. It is only used inside `queryFn`, and
   PlatformDataApi contains a circular Auth0 client that breaks key serialization.
   Real cache inputs are still included in each key.  */
import { queryOptions } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { ISharedDataApi } from "../types";

// SENSOR QUERIES

export const getSensorsPerSubstationOptions = (
  propAiApi: ISharedDataApi | null,
  substationUid?: string
) =>
  queryOptions({
    queryKey: ["sensors", substationUid],
    queryFn: () => propAiApi?.getSensorsBySubstation(substationUid),
    enabled: !!substationUid,
    // Don't flatten here - let the hooks handle their own flattening
    // This preserves the original FlexStation structure like in develop branch
    select(data) {
      return data; // Return FlexStations as-is, just like in develop
    },
  });

export const getSensorsDataPerSubstation = (
  propAiApi: ISharedDataApi | null,
  substationUid?: string,
  sensorUid?: string,
  fromDate?: DateTime | null,
  toDate?: DateTime | null
) =>
  queryOptions({
    queryKey: [
      "sensors",
      substationUid,
      sensorUid,
      fromDate?.toISO(),
      toDate?.toISO(),
      fromDate!.toISO,
      toDate!.toISO,
    ],
    refetchInterval: 1000 * 60 * 5, // 5 minutes
    queryFn: () =>
      propAiApi?.getSensorData(substationUid, sensorUid, fromDate!.toISO()!, toDate!.toISO()!),
    enabled: !!substationUid && !!sensorUid && !!fromDate && !!toDate,
  });

export const getAverageSensorDataPerSubstationOptions = (
  propAiApi: ISharedDataApi | null,
  substationUid?: string,
  sensorTypeTechnicalName?: string,
  fromDate?: DateTime | null,
  toDate?: DateTime | null
) =>
  queryOptions({
    queryKey: [
      "sensors",
      "average",
      substationUid,
      sensorTypeTechnicalName,
      fromDate?.toISO(),
      toDate?.toISO(),
      fromDate!.toISO,
      toDate!.toISO,
    ],
    queryFn: () =>
      propAiApi?.getAverageSensorData(
        substationUid,
        sensorTypeTechnicalName,
        fromDate!.toISO()!,
        toDate!.toISO()!
      ),
    enabled: !!substationUid && !!sensorTypeTechnicalName && !!fromDate && !!toDate,
  });

// TEMPERATURE QUERIES

export const getStandardTimeRanges = () => {
  const now = DateTime.now();

  return {
    // 7 days
    sevenDays: {
      startDate: now.minus({ days: 7 }).startOf("day"),
      endDate: now.plus({ hour: 1 }).startOf("hour"),
    },
    // 1 day
    oneDay: {
      startDate: now.minus({ hours: 24 }).startOf("hour"),
      endDate: now.plus({ hour: 1 }).startOf("hour"),
    },
  };
};

/**
 * Fetches backend-computed indoor-temperature averages for 1h, 24h, 7d, and 30d.
 *
 * Replaces the old per-window/current-temperature queries and frontend averaging.
 * Enabled for either Platform substation/network scope or PropAI flexstation scope.
 *
 * `current` is the last 1h value, or `null` if that hour had no reading.
 */
export const getTemperatureAveragesOptions = (
  dataApi: ISharedDataApi | null,
  substationUid?: string,
  flexstationUid?: string | null,
  networkUid?: string
) =>
  queryOptions({
    queryKey: ["temperature", "averages", networkUid, substationUid, flexstationUid],
    queryFn: async () => {
      const averages = await dataApi!.getTemperatureAverages(
        substationUid!,
        flexstationUid,
        networkUid
      );
      return {
        substation_uid: substationUid!,
        current: averages.last1Hour,
        avgTemp24h: averages.last24Hours,
        avgTemp7d: averages.last7Days,
        avgTemp30d: averages.last30Days,
      };
    },
    enabled: !!dataApi && !!substationUid && (!!flexstationUid || !!networkUid),
    staleTime: 5 * 60 * 1000,
  });

export const getTimeSeriesTemperaturesOptions = (
  propAiApi: ISharedDataApi | null,
  substationUid: string
) => {
  const ranges = getStandardTimeRanges();

  return queryOptions({
    queryKey: [
      "temperature",
      "timeseries-data",
      substationUid,
      ranges.sevenDays.startDate.toISO(),
      ranges.oneDay.startDate.toISO(),
    ],
    queryFn: async () => {
      const [timeSeries7d, timeSeries24h] = await Promise.all([
        propAiApi.getAverageSensorData(
          substationUid,
          "living_air_temperature",
          ranges.sevenDays.startDate.toISO(),
          ranges.sevenDays.endDate.toISO()
        ),
        propAiApi.getAverageSensorData(
          substationUid,
          "living_air_temperature",
          ranges.oneDay.startDate.toISO(),
          ranges.oneDay.endDate.toISO()
        ),
      ]);

      return {
        substation_uid: substationUid,
        timeSeries7d: timeSeries7d || [],
        timeSeries24h: timeSeries24h || [],
      };
    },
    enabled: !!propAiApi && !!substationUid,
    staleTime: 5 * 60 * 1000,
  });
};
export const getEnergyKpisBySubstationOptions = (
  dataApi: ISharedDataApi | null,
  substationUid?: string,
  fromDate?: DateTime | null,
  networkUid?: string
) =>
  queryOptions({
    queryKey: [networkUid, "substation", substationUid, "energy", "kpis", fromDate?.toISO()],
    queryFn: () => dataApi?.getEnergyKpisBySubstation(substationUid, fromDate?.toISO(), networkUid),
    enabled: !!substationUid,
  });

export const getEquivalentPowerAggregatePerSubstationOptions = (
  propAiApi: ISharedDataApi | null,
  substationUid?: string,
  fromDate?: DateTime,
  toDate?: DateTime,
  networkUid?: string
) =>
  queryOptions({
    queryKey: [
      "equivalent-power-aggregate",
      substationUid,
      fromDate?.toISO(),
      toDate?.toISO(),
      networkUid,
    ],
    queryFn: () =>
      propAiApi?.getEquivalentPowerAggregateBySubstation(
        substationUid,
        fromDate?.toISO(),
        toDate?.toISO(),
        networkUid
      ),
    enabled: !!substationUid && !!fromDate && !!toDate && !!propAiApi,
  });

export const getFlexStationParametersOptions = (
  dataApi: ISharedDataApi | null,
  flexstationUid: string | null,
  substationUid?: string | null,
  networkUid?: string | null
) =>
  queryOptions({
    queryKey: [
      "network",
      networkUid,
      "substations",
      substationUid,
      "flex-stations",
      flexstationUid,
      "parameters",
    ],
    queryFn: () => {
      return dataApi?.getFlexStationParameters(flexstationUid!, substationUid!, networkUid!);
    },
    enabled: !!dataApi && !!flexstationUid,
  });

export const getStationAdapterConfigOptions = (
  dataApi: ISharedDataApi | null,
  flexstationUid?: string,
  substationUid?: string,
  networkUid?: string
) =>
  queryOptions({
    queryKey: ["station-adapter-config", flexstationUid, substationUid, networkUid],
    queryFn: () => dataApi!.getStationAdapterConfig(flexstationUid!, substationUid, networkUid),
    enabled: !!dataApi && !!flexstationUid,
  });

export const getFlexControlRequestsDataOptions = (
  dataApi: ISharedDataApi | null,
  flexstationUid?: string,
  fromDate?: DateTime | null,
  toDate?: DateTime | null,
  substationUid?: string,
  networkUid?: string
) =>
  queryOptions({
    queryKey: [
      "flex-control-requests-data",
      flexstationUid,
      fromDate?.toISO(),
      toDate?.toISO(),
      substationUid,
      networkUid,
    ],
    queryFn: () =>
      dataApi?.getFlexControlRequestsData(
        flexstationUid!,
        fromDate?.toISO(),
        toDate?.toISO(),
        substationUid,
        networkUid
      ),
    enabled: !!flexstationUid && !!fromDate && !!toDate && !!dataApi,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
